/* Face ID / Touch ID passkey helpers for the admin PWA.
 *
 * Wraps navigator.credentials to expose two flows:
 *   - registerPasskey({ getToken, apiBase }) — called after a password login
 *   - loginWithPasskey({ email, apiBase }) — called on the login page
 *
 * Base64url ↔ ArrayBuffer conversions are inline. Errors that mean "user
 * cancelled" are turned into a soft `cancelled: true` result so the caller
 * doesn't show a toast for a benign abort.
 */
import axios from "axios";

const b64urlToBuf = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

const bufToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const isCancel = (err) =>
  err && (err.name === "NotAllowedError" || err.name === "AbortError" || err.name === "InvalidStateError");

export const isPasskeySupported = () =>
  typeof window !== "undefined"
  && !!window.PublicKeyCredential
  && typeof window.PublicKeyCredential === "function";

export const isPlatformAuthenticatorAvailable = async () => {
  try {
    if (!isPasskeySupported()) return false;
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { return false; }
};

/* Register a new passkey. Requires the user to already be signed in via password
 * — we send the JWT in the Authorization header via the shared axios instance. */
export const registerPasskey = async ({ apiBase }) => {
  const beginRes = await axios.post(`${apiBase}/api/admin/webauthn/register/begin`, {});
  const options = beginRes.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  };
  let cred;
  try {
    cred = await navigator.credentials.create({ publicKey });
  } catch (e) {
    if (isCancel(e)) return { cancelled: true };
    throw e;
  }
  if (!cred) return { cancelled: true };
  const payload = {
    credential: {
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(cred.response.clientDataJSON),
        attestationObject: bufToB64url(cred.response.attestationObject),
      },
    },
    challenge: options.challenge,
  };
  const done = await axios.post(`${apiBase}/api/admin/webauthn/register/complete`, payload);
  return { ok: true, credential_id: done.data.credential_id };
};

/* Sign in with a passkey. Returns { access_token, email } on success. */
export const loginWithPasskey = async ({ email, apiBase }) => {
  const beginRes = await axios.post(`${apiBase}/api/admin/webauthn/login/begin`, { email });
  const options = beginRes.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  };
  let assertion;
  try {
    assertion = await navigator.credentials.get({ publicKey });
  } catch (e) {
    if (isCancel(e)) return { cancelled: true };
    throw e;
  }
  if (!assertion) return { cancelled: true };
  const payload = {
    credential: {
      id: assertion.id,
      rawId: bufToB64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufToB64url(assertion.response.clientDataJSON),
        authenticatorData: bufToB64url(assertion.response.authenticatorData),
        signature: bufToB64url(assertion.response.signature),
        userHandle: assertion.response.userHandle ? bufToB64url(assertion.response.userHandle) : null,
      },
    },
    challenge: options.challenge,
  };
  const done = await axios.post(`${apiBase}/api/admin/webauthn/login/complete`, payload);
  return { ok: true, ...done.data };
};

export const passkeyStatus = async ({ apiBase }) => {
  const res = await axios.get(`${apiBase}/api/admin/webauthn/status`);
  return res.data;
};

export const passkeyAvailable = async ({ apiBase }) => {
  try {
    const res = await axios.get(`${apiBase}/api/admin/webauthn/available`);
    return !!res.data.available;
  } catch { return false; }
};

export const deletePasskey = async ({ apiBase, credentialId }) => {
  await axios.delete(`${apiBase}/api/admin/webauthn/credentials/${encodeURIComponent(credentialId)}`);
  return true;
};
